import { useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router";

/**
 * Maximum number of categories one bulk update may carry. Mirrors the backend
 * contract (`categoryIds`: 1..200) — the list page disables the menu entry above
 * it rather than silently truncating.
 */
export const BULK_UPDATE_MAX_CATEGORIES = 200;

/** What the page needs about each selected category: the id it writes, the title it shows. */
export type BulkUpdateCategorySelectionItem = {
  id: string;
  title: string | null;
};

/** The router `state` shape the list page hands over on navigation. */
export type BulkUpdateCategorySelectionState = {
  bulkUpdateCategories?: BulkUpdateCategorySelectionItem[];
};

/** Shared prefix of every per-league key — what the logout sweep matches on. */
const BULK_UPDATE_SELECTION_STORAGE_PREFIX = "bulkUpdateTournamentCategories:";

/**
 * Per-league storage key. Scoping by league keeps two leagues' selections from
 * overwriting each other in the same tab, and makes the "wrong league" case
 * (deep link into league B carrying league A's ids) impossible.
 */
export const getBulkUpdateSelectionStorageKey = (leagueId: string): string =>
  `${BULK_UPDATE_SELECTION_STORAGE_PREFIX}${leagueId}`;

const isSelectionItem = (value: unknown): value is BulkUpdateCategorySelectionItem => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { id?: unknown; title?: unknown };

  return (
    typeof candidate.id === "string" &&
    (candidate.title === null || typeof candidate.title === "string")
  );
};

/** Narrow an unknown router `state` to the selection it may carry. */
export const parseBulkUpdateSelectionState = (
  state: unknown
): BulkUpdateCategorySelectionItem[] | null => {
  if (typeof state !== "object" || state === null) return null;
  const candidate = (state as BulkUpdateCategorySelectionState).bulkUpdateCategories;
  if (!Array.isArray(candidate)) return null;

  const items = candidate.filter(isSelectionItem);

  return items.length > 0 ? items : null;
};

/** `sessionStorage`, or `undefined` where the browser refuses to hand it over. */
const safeSessionStorage = (): Storage | undefined => {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
};

/**
 * Read a persisted selection. Storage access is wrapped because Safari's private
 * mode throws on `sessionStorage` access, and a stored blob written by an older
 * build may not parse.
 */
export const readStoredBulkUpdateSelection = (
  leagueId: string,
  storage: Storage | undefined = safeSessionStorage()
): BulkUpdateCategorySelectionItem[] | null => {
  if (!storage) return null;

  try {
    const raw = storage.getItem(getBulkUpdateSelectionStorageKey(leagueId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const items = parsed.filter(isSelectionItem);

    return items.length > 0 ? items : null;
  } catch {
    return null;
  }
};

/** Persist a selection so a page refresh (or a hard reload) keeps the batch. */
export const writeStoredBulkUpdateSelection = (
  leagueId: string,
  items: BulkUpdateCategorySelectionItem[],
  storage: Storage | undefined = safeSessionStorage()
): void => {
  if (!storage) return;

  try {
    storage.setItem(getBulkUpdateSelectionStorageKey(leagueId), JSON.stringify(items));
  } catch {
    // A full / unavailable storage only costs refresh-survival, never the flow.
  }
};

/** Forget a persisted selection (after a successful update, or on cancel). */
export const clearStoredBulkUpdateSelection = (
  leagueId: string,
  storage: Storage | undefined = safeSessionStorage()
): void => {
  if (!storage) return;

  try {
    storage.removeItem(getBulkUpdateSelectionStorageKey(leagueId));
  } catch {
    // Nothing to do — the selection is dropped from memory either way.
  }
};

/**
 * Drop EVERY league's persisted selection.
 *
 * The per-league keys are written under a shared prefix and would otherwise
 * outlive the session that created them: the next user on the same tab could
 * deep-link into `/bulk-edit` and revive a batch of category ids they never
 * picked. Part of the logout teardown, next to the other `clear*` calls.
 */
export const clearAllStoredBulkUpdateSelections = (
  storage: Storage | undefined = safeSessionStorage()
): void => {
  if (!storage) return;

  try {
    // Collect first: removing while iterating `key(i)` re-indexes the store.
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(BULK_UPDATE_SELECTION_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Nothing to do — a storage the browser refuses to read holds nothing we can leak.
  }
};

export type BulkUpdateCategorySelection = {
  /** The categories the page will write. Empty when nothing could be recovered. */
  categories: BulkUpdateCategorySelectionItem[];
  /** `true` when neither the router state nor storage held a selection — the page redirects. */
  isMissing: boolean;
  /** Drop the persisted selection (called after a successful update). */
  clear: () => void;
};

/**
 * Recover the categories a bulk update applies to.
 *
 * Hundreds of ids are too many for a URL, so the list page hands them over in
 * two places at once: react-router `state` (instant, survives an in-app
 * navigation) and `sessionStorage` (survives F5 / a restored tab). State wins
 * when present and is mirrored into storage; a refresh then falls back to
 * storage. When neither holds anything the page has no batch to act on and
 * sends the user back to the list.
 *
 * The recovered list is captured once per mount, so a later `clear()` (or a
 * storage write from another tab) can't yank the batch out from under an open
 * review modal.
 */
export const useBulkUpdateCategorySelection = (leagueId: string): BulkUpdateCategorySelection => {
  const { state } = useLocation();
  const fromState = parseBulkUpdateSelectionState(state);

  // `useRef` (not `useState`) so the very first render already has the value —
  // the page decides between "render" and "redirect" on that render.
  const resolved = useRef<BulkUpdateCategorySelectionItem[] | null>(null);
  if (resolved.current === null) {
    const recovered = fromState ?? readStoredBulkUpdateSelection(leagueId);
    if (recovered && fromState) writeStoredBulkUpdateSelection(leagueId, recovered);
    resolved.current = recovered ?? [];
  }

  const categories = resolved.current;

  const clear = useCallback(() => clearStoredBulkUpdateSelection(leagueId), [leagueId]);

  return useMemo(
    () => ({ categories, isMissing: categories.length === 0, clear }),
    [categories, clear]
  );
};
